<template>
  <section class="page">
    <h2 class="page__title">{{ $t('chpo.orders.title') }}</h2>

    <p v-if="loading">{{ $t('chpo.orders.loading') }}</p>
    <p v-else-if="error" style="color:#D81717">{{ $t('chpo.orders.errorPrefix') }}: {{ error }}</p>


    <div v-if="editing" class="order-edit">
      <h3 class="order__title">{{ $t('chpo.orders.orderHeading') }}</h3>
      <textarea
        class="form-textarea title-textarea"
        v-model="form.subtitle"
        :placeholder="$t('chpo.orders.subtitlePh')"
      ></textarea>
      <textarea
        class="form-textarea large"
        v-model="form.content"
        :placeholder="$t('chpo.orders.contentPh')"
      ></textarea>
    </div>


    <article v-else class="order">
      <h3 class="order__title">{{ $t('chpo.orders.orderHeading') }}</h3>
      <div class="order__subtitle-view">{{ viewSubtitle }}</div>
      <div class="order__content">{{ viewContent }}</div>
    </article>


    <div class="page__footer">
      <ActionButton v-if="!editing" :outlined="true" :borderColor="'#111827'" :text="$t('chpo.orders.editBtn')" @click="startEdit" />
      <template v-else>
        <ActionButton :color="'#2A8A1B'" :disabled="saving" :text="$t('nav.save')" @click="save" />
        <ActionButton :outlined="true" :borderColor="'#111827'" :disabled="saving" :text="$t('nav.cancel')" @click="cancelEdit" />
      </template>
      <ViewNavButtons class="page__footer-nav" :prev-text="$t('nav.back')" :next-text="$t('nav.next')" :prev-to="{ path: '/chpo/personnel' }" :next-to="{ path: '/chpo/map' }" />
    </div>
  </section>
</template>

<script>
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import { getChpoOrderFromContext, updateChpoOrder } from '@/api/chpoOrders'

export default {
  name: 'OrdersView',
  components: { ViewNavButtons, ActionButton },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      order: null, // { content, subtitle?, updatedAt? }
      editing: false,
      form: { content: '', subtitle: '' },
    }
  },
  computed: {
    viewContent() {
      return this.form.content || this.defaultContent()
    },
    viewSubtitle() {
      return this.form.subtitle || this.defaultSubtitle()
    },
  },
  mounted() {
    this.loadOrder()
  },
  methods: {
    defaultSubtitle() { return this.$t('chpo.orders.defaultSubtitle') },
    defaultContent() {
      return this.$t('chpo.orders.defaultContent')
    },
    async loadOrder() {
      this.loading = true
      this.error = null
      try {
        const order = await getChpoOrderFromContext()
        if (order && order.text) {
          this.order = { id: order.id, rawText: order.text }
          const lines = String(order.text).split(/\r?\n/).filter(l => l.trim().length)
          const subtitle = lines.length ? lines[0] : this.defaultSubtitle()
          const body = lines.slice(1).join('\n\n') || this.defaultContent()
          this.form.subtitle = subtitle
          this.form.content = body
        } else {
          this.order = null
          this.form.subtitle = this.defaultSubtitle()
          this.form.content = this.defaultContent()
        }
      } catch (e) {
        this.error = e?.message || String(e)
      } finally {
        this.loading = false
      }
    },
    startEdit() {
      this.editing = true
      // содержимое уже в form
    },
    cancelEdit() {
      this.editing = false
      // откат к сохранённому order
      if (this.order && this.order.rawText) {
        const lines = String(this.order.rawText).split(/\r?\n/).filter(l => l.trim().length)
        this.form.subtitle = lines.length ? lines[0] : this.defaultSubtitle()
        this.form.content = lines.slice(1).join('\n\n') || this.defaultContent()
      } else {
        this.form.subtitle = this.defaultSubtitle()
        this.form.content = this.defaultContent()
      }
    },
    async save() {
      if (!this.order) {
        // нет id приказа — пока просто создаём локально (можно расширить createChpoOrder, если появится)
        this.editing = false
        return
      }
      this.saving = true
      this.error = null
      try {
        const textCombined = [this.form.subtitle, this.form.content].filter(Boolean).join('\n')
        const updated = await updateChpoOrder(this.order.id, { text: textCombined })
        this.order = { id: updated.id, rawText: updated.text }
        this.editing = false
      } catch (e) {
        this.error = e?.message || String(e)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page { background: #FFFFFF; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 24px; }
.page__title { margin: 0; color: #111827; font-size: 24px; font-weight: 700; line-height: 33.6px; }
.page__actions { display: flex; gap: 12px; }

.order__title { margin: 0 0 8px 0; color: #000; font-size: 22px; font-weight: 800; line-height: 32px; }
.order__subtitle-view { white-space: pre-wrap; color: #000; font-size: 24px; font-weight: 700; line-height: 26px; margin-bottom: 12px; }
.order__content { white-space: pre-wrap; color: #000; font-size: 20px; line-height: 24px; }

.order-edit { display: grid; gap: 12px; }
.form-textarea { width: 100%; padding: 12px; border: 1px solid #E5E7EB; border-radius: 10px; font-size: 14px; line-height: 22px; resize: vertical; }
.form-textarea.title-textarea { font-weight: 700; min-height: 64px; }
.form-textarea.large { min-height: 320px; }

.page__footer { margin-top: 16px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.page__footer-nav { margin-left: auto; }
</style>
